/** 外层已有的安全区与返回按钮由外层提供，不猜测设备状态栏高度。 */
type FrameHostOptions = {
  topInset?: () => number;
  readBackTop?: () => number | undefined;
  onGeometry?: (geometry: { height: number; offsetTop: number } | null) => void;
};

/** 常小二 iOS 可见区域通信外层，协议第 1 版；复制到外层 Vue 项目的 src/utils 即可使用。 */
export function attachCcxFrameHost(iframe: HTMLIFrameElement, childOrigin: string, options: FrameHostOptions = {}) {
  const url = new URL(childOrigin);
  if (!/^https?:$/.test(url.protocol) || url.origin !== childOrigin) throw new Error("Exact child origin required");
  const win = iframe.ownerDocument.defaultView!;
  const doc = win.document;
  const ios = /iPad|iPhone|iPod/.test(win.navigator.userAgent) ||
    (win.navigator.platform === "MacIntel" && win.navigator.maxTouchPoints > 1);
  let session = "";
  let sequence = 0;
  let viewportSequence = 0;
  let traceUntil = 0;
  let traceCount = 0;
  let running = true;
  let closing = false;
  let frame = 0;
  let until = 0;
  let lastSent = "";
  let lastAt = 0;
  let sent = 0;
  let closeTimer = 0;
  let closePromise: Promise<void> | null = null;
  let finishClose: (() => void) | null = null;
  let lastGeometry: { height: number; offsetTop: number } | null = null;
  // 同一窗口宽度下记录未平移时观测到的最大布局高度，不写死设备或键盘高度。
  // 转向/分屏改变宽度时重建基线，避免沿用另一种布局的尺寸。
  let layoutWidth = win.innerWidth;
  let unshiftedHeight = 0;
  let coordinateMode: "layout" | "ios-resized-scroll" = "layout";
  const post = (type: string, geometry?: { height: number; offsetTop: number }) => {
    if (!session) return;
    iframe.contentWindow?.postMessage({ channel: "ccx:viewport", version: 1, session, sequence: ++sequence, type, ...geometry }, childOrigin);
  };
  const measure = () => {
    frame = 0;
    if (!running || closing || !ios || doc.hidden || !session || !iframe.isConnected) return;
    // 必须在外层页面测量；所有边界都使用外层布局视口中的 CSS 像素坐标。
    const viewport = win.visualViewport;
    const reportedTop = Math.max(0, viewport?.offsetTop || 0);
    const unscaled = !!viewport && Math.abs(viewport.scale - 1) < 0.001;
    if (Math.abs(win.innerWidth - layoutWidth) > 1) {
      layoutWidth = win.innerWidth;
      unshiftedHeight = 0;
    }
    if (unscaled && Math.abs(win.scrollY) < 1 && reportedTop < 1 &&
        Math.abs(viewport.height - win.innerHeight) < 1) {
      unshiftedHeight = Math.max(unshiftedHeight, win.innerHeight);
    }
    // iOS 可能把同一段页面上移同时计入 scrollY 和 offsetTop。
    // 底部输入：844 → innerHeight=440、可见高440、scrollY=404。
    // 中间输入：844 → innerHeight=504、可见高440、scrollY=340。
    // 后一种两高度不相等，但 innerHeight + scrollY 仍等于已测量的原高度；
    // iframe 的 rect.top 已扣除了340，不能再把 offsetTop=340 加一次。
    // 只有尺寸闭合且两个偏移一致时去重，保留普通视口平移和缩放的原路径。
    const resizedByScroll = !!viewport && unshiftedHeight > 0 &&
      win.innerHeight > viewport.height &&
      Math.abs(win.innerHeight + win.scrollY - unshiftedHeight) < 1;
    const repeatedScroll = unscaled && win.scrollY > 0 &&
      (Math.abs(viewport.height - win.innerHeight) < 1 || resizedByScroll) &&
      Math.abs(reportedTop - win.scrollY) < 1;
    const top = repeatedScroll ? 0 : reportedTop;
    const requestedInset = options.topInset?.() || 0;
    const inset = Number.isFinite(requestedInset) ? Math.max(0, requestedInset) : 0;
    coordinateMode = repeatedScroll ? "ios-resized-scroll" : "layout";
    const height = Math.min(win.innerHeight, viewport?.height || win.innerHeight);
    const rect = iframe.getBoundingClientRect();
    const scale = iframe.offsetHeight > 0 ? rect.height / iframe.offsetHeight : 0;
    if (scale > 0 && Number.isFinite(scale) && height > 0) {
      const contentTop = rect.top + iframe.clientTop * scale;
      // 只收紧顶部边界，底部仍是实际可见区底部；不能把安全区再加到底部。
      const offsetTop = Math.max(0, (top + inset - contentTop) / scale);
      const bottom = Math.min(iframe.clientHeight, (top + height - contentTop) / scale);
      const geometry = { offsetTop, height: Math.max(0, bottom - offsetTop) };
      // 原生动画短暂遮住整个 iframe 时，不将助手高度压成零，避免出现瞬时白屏。
      if (geometry.height > 0 && Number.isFinite(geometry.height)) {
        const signature = JSON.stringify(geometry);
        const now = win.performance.now();
        if (signature !== lastSent || now - lastAt >= 750) {
          options.onGeometry?.(geometry);
          post("viewport", geometry); viewportSequence = sequence; lastGeometry = geometry; lastSent = signature; lastAt = now; sent++;
        }
        if (win.performance.now() < traceUntil && traceCount < 240) {
          // 时间使用同设备的 timeOrigin + now，便于与子页收件及绘制阶段对齐。
          const backTop = options.readBackTop?.();
          iframe.contentWindow?.postMessage({ channel: "ccx:viewport", version: 1, session,
            sequence: ++sequence, type: "trace-frame", trace: {
              at: win.performance.timeOrigin + win.performance.now(), sequence: viewportSequence,
              offsetTop: geometry.offsetTop, height: geometry.height, scrollY: win.scrollY,
              viewportTop: reportedTop, viewportHeight: viewport?.height || win.innerHeight,
              innerHeight: win.innerHeight, frameTop: rect.top, frameScale: scale, topInset: inset,
              ...(Number.isFinite(backTop) ? { backTop } : {}),
            } }, childOrigin);
          traceCount++;
        }
      }
    }
    if (win.performance.now() < until) frame = win.requestAnimationFrame(measure);
  };
  const settle = (ms = 600) => {
    if (!running || closing || !ios) return;
    until = Math.max(until, win.performance.now() + ms);
    if (!frame) frame = win.requestAnimationFrame(measure);
  };
  const message = (event: MessageEvent) => {
    const data = event.data;
    if (!running || !ios || event.source !== iframe.contentWindow || event.origin !== childOrigin ||
        !data || typeof data !== "object" || data.channel !== "ccx:viewport" || data.version !== 1 ||
        typeof data.session !== "string" || !/^[a-zA-Z0-9-]{8,100}$/.test(data.session)) return;
    if (data.type === "suspended" && data.session === session) { finishClose?.(); return; }
    if (closing) return;
    if (data.type === "ready") {
      if (session !== data.session) { session = data.session; sequence = 0; lastSent = ""; }
      settle(1500);
    } else if (data.session === session && data.type === "trace-start") {
      traceUntil = win.performance.now() + 30000; traceCount = 0; settle(100);
    } else if (data.session === session && data.type === "trace-stop") traceUntil = 0;
    else if (data.session === session && data.type === "measure") settle(1500);
    else if (data.session === session && data.type === "ping") settle(0);
    else if (data.session === session && data.type === "inactive") {
      session = ""; lastSent = ""; traceUntil = 0; win.cancelAnimationFrame(frame); frame = 0;
    }
  };
  const viewportEvent = () => settle();
  const requestReady = () => iframe.contentWindow?.postMessage({ channel: "ccx:viewport", version: 1, type: "request-ready" }, childOrigin);
  const load = () => { session = ""; lastSent = ""; lastGeometry = null; requestReady(); };
  // 旧版 TS lib.dom.d.ts 没有 ResizeObserver 类型，从 window 取值兜底。
  const ResizeObserverCtor = (win as any).ResizeObserver;
  const resizeObserver = typeof ResizeObserverCtor === "function" ? new ResizeObserverCtor(viewportEvent) : null;
  const close = (): Promise<void> => {
    if (closePromise) return closePromise;
    closing = true; traceUntil = 0; win.cancelAnimationFrame(frame); frame = 0;
    options.onGeometry?.(null);
    closePromise = new Promise(resolve => {
      finishClose = () => { win.clearTimeout(closeTimer); finishClose = null; resolve(); };
      if (!ios || !session || !running) { finishClose(); return; }
      // 路由离开前等待助手释放焦点的回执；超时仍允许离开，避免阻塞导航。
      post("suspend"); closeTimer = win.setTimeout(() => finishClose?.(), 250);
    });
    return closePromise;
  };
  const pageHide = () => { void close(); };
  const pageShow = () => {
    if (!running) return;
    finishClose?.(); closing = false; closePromise = null; session = ""; lastSent = "";
    requestReady();
  };
  const visibility = () => {
    if (doc.hidden) { win.cancelAnimationFrame(frame); frame = 0; }
    else if (!closing) { requestReady(); settle(1500); }
  };
  if (ios) {
    win.addEventListener("message", message);
    win.addEventListener("resize", viewportEvent);
    win.addEventListener("scroll", viewportEvent, true);
    win.addEventListener("orientationchange", viewportEvent);
    win.addEventListener("pagehide", pageHide);
    win.addEventListener("pageshow", pageShow);
    doc.addEventListener("visibilitychange", visibility);
    win.visualViewport?.addEventListener("resize", viewportEvent);
    win.visualViewport?.addEventListener("scroll", viewportEvent);
    iframe.addEventListener("load", load);
    resizeObserver?.observe(iframe);
    requestReady();
  }
  return {
    close,
    // 调试摘要不包含网址、登录凭据、输入文本、账号或门店信息。
    status: () => ({ version: 1, revision: "HOST-0920-5", ios, connected: !!session, closing, sent, coordinateMode, geometry: lastGeometry }),
    dispose: () => {
      if (!running) return;
      void close(); running = false; finishClose?.();
      win.cancelAnimationFrame(frame); resizeObserver?.disconnect();
      win.removeEventListener("message", message);
      win.removeEventListener("resize", viewportEvent);
      win.removeEventListener("scroll", viewportEvent, true);
      win.removeEventListener("orientationchange", viewportEvent);
      win.removeEventListener("pagehide", pageHide);
      win.removeEventListener("pageshow", pageShow);
      doc.removeEventListener("visibilitychange", visibility);
      win.visualViewport?.removeEventListener("resize", viewportEvent);
      win.visualViewport?.removeEventListener("scroll", viewportEvent);
      iframe.removeEventListener("load", load);
    },
  };
}
